'use client'

import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import * as React from 'react'

import { cn } from '../lib/utils'

interface TooltipProps {
  content: React.ReactNode
  children: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  delay?: number
  openDelay?: number
  className?: string
  sideOffset?: number
  hidden?: boolean
  hideArrow?: boolean
  container?: HTMLElement | null
}

export const TooltipProvider = TooltipPrimitive.Provider
export const TooltipRoot = TooltipPrimitive.Root
export const TooltipTrigger = TooltipPrimitive.Trigger
export const TooltipContent = TooltipPrimitive.Content

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      children,
      content,
      side = 'top',
      align = 'center',
      delay,
      openDelay = delay || 200,
      className,
      sideOffset = 4,
      hidden = false,
      hideArrow = false,
      container,
      ...props
    },
    ref
  ) => {
    return (
      <TooltipProvider 
        delayDuration={openDelay}
        data-slot="tooltip-provider"
      >
        <TooltipRoot 
          data-slot="tooltip"
        >
          <TooltipTrigger asChild data-slot="tooltip-trigger">{children}</TooltipTrigger>
          <TooltipPrimitive.Portal container={container}>
            <TooltipContent
              autoFocus={false}
              hidden={hidden}
              ref={ref}
              side={side}
              align={align}
              sideOffset={sideOffset}
              data-slot="tooltip-content"
              className={cn(
                "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance",
                // Light mode: black background with white text
                "bg-black text-white",
                // Dark mode: white background with black text  
                "dark:bg-white dark:text-black",
                className
              )}
              {...props}
            >
              <div className="relative z-10 flex items-center gap-2">
                <div className="flex-1">
                  {content}
                </div>
              </div>
              
              {!hideArrow && <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] bg-black fill-black dark:bg-white dark:fill-white" />}
            </TooltipContent>
          </TooltipPrimitive.Portal>
        </TooltipRoot>
      </TooltipProvider>
    )
  }
)

TooltipContent.displayName = 'TooltipContent'
